/**
 * networkMonitor.ts
 *
 * Wraps navigator.onLine and the browser online/offline events.
 * The SyncWatcher uses this to know when to attempt sync and when to back off.
 */

type NetworkListener = () => void;

class NetworkMonitor {
  private onlineListeners: Set<NetworkListener> = new Set();
  private offlineListeners: Set<NetworkListener> = new Set();
  private initialized = false;

  /** Start listening to browser network events. Call once at app startup. */
  initialize(): void {
    if (this.initialized || typeof window === "undefined") return;
    this.initialized = true;

    window.addEventListener("online", () => {
      this.onlineListeners.forEach((fn) => fn());
    });

    window.addEventListener("offline", () => {
      this.offlineListeners.forEach((fn) => fn());
    });
  }

  /** Returns true if the browser believes it has a network connection. */
  isOnline(): boolean {
    if (typeof navigator === "undefined") return true;
    return navigator.onLine;
  }

  /** Register a callback to fire when the browser comes back online. */
  onOnline(fn: NetworkListener): () => void {
    this.onlineListeners.add(fn);
    return () => this.onlineListeners.delete(fn);   // returns unsubscribe function
  }

  /** Register a callback to fire when the browser goes offline. */
  onOffline(fn: NetworkListener): () => void {
    this.offlineListeners.add(fn);
    return () => this.offlineListeners.delete(fn);
  }
}

// Singleton — import this everywhere
export const networkMonitor = new NetworkMonitor();
